import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Modal, Space} from 'antd';
import {useNavigate} from 'react-router-dom';
import {connect} from 'react-redux';
import {StyledTeam} from './StyledTeam';
import AuthenticationWindow from '../../../CardsPage/AuthenticationWindow';
import AllTab from '../../../TeamPage/tabs/AllTab';
import DisableUserModal from '../../../TeamPage/DisableUserModal';
import AddUserModal from '../../../TeamPage/AddUserModal';
import {StyledTableFiltersDivider} from '../../../TransactionsPage/TableFilters/StyledTableFilters';
import {
  DEFAULT_MODAL_PROPS,
  DEFAULT_TABLE_DATA,
  DEFAULT_TABLE_QUERY,
  SCAActionsConstants,
  userStatusesConstants,
  userGroupConstants
} from '../../../../../constants';
import {alertActions} from '../../../../../state/actions/alert.actions';
import {teamActions} from '../../../../../state/actions/team.actions';
import {firebaseEvents} from '../../../../../snippets/firebase';
import routes from '../../../../../routes/routes.json';
import {
  objectHelpers,
  scaHelpers,
  systemHelpers,
  tabHelpers,
  teamsHelpers
} from '../../../../../utils/helpers';
import {useHasAccess} from '../../../../../hooks';

const {logEvent} = systemHelpers;
const {getObjProp: gObjProp} = objectHelpers;

const {DISABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION, ENABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION} = SCAActionsConstants;

const {DISABLED: DISABLED_STATUS, ACTIVE: ENABLED_STATUS} = userStatusesConstants;

const {ALL} = userGroupConstants;

const Team = ({
  dispatch,

  employeeEmail,
  isSupport,

  createEmployee,
  deleteEmployee,
  getEmployees,
  resendInvite,
  updateEmployee,
  updateEmployeeStatus,

  ...rest
}) => {
  const navigate = useNavigate();
  const [t] = useTranslation(['main', 'teams']);
  const [tableData, setTableData] = useState(DEFAULT_TABLE_DATA);
  const [queries, setQueries] = useState({...DEFAULT_TABLE_QUERY, user_group: ALL});
  const [loading, setLoading] = useState(true);
  const [inviteModalProps, setInviteModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [authWindowProps, setAuthWindowProps] = useState({open: false});
  const [isOpenDisableModal, setIsOpenDisableModal] = useState(false);
  const [activeAction, setActiveAction] = useState({action: '', email: '', isTerminateSubscriptions: false});
  const [selectedEmployee, setSelectedEmployee] = useState(undefined);
  const hasCreateAccess = useHasAccess('POST_COMPANY_TEAM_EMPLOYEE_CREATE');

  const trans = (key) => t(`teams:${key}`);

  useEffect(() => {
    loadData({
      initial: true,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startLoading = () => setLoading(true);

  const finishLoading = () => setLoading(false);

  const loadData = ({query = null, initial = false, updateCurrentPage = false}) => {
    const viewQuery = tabHelpers.getViewQuery({initial, updateCurrentPage, query, queries, single: true});
    setQueries(viewQuery);
    startLoading();

    getEmployees({
      query: viewQuery,
      successCallback: (response) => {
        let updatedViewTableDate = tabHelpers.getUpdatedTableData({
          tableData,
          response,
          initial,
          rowsKey: 'users',
          updateCurrentPage
        });

        setTableData(updatedViewTableDate);
        finishLoading();
      },
      errorCallback: finishLoading
    })
  }

  const handleOnPaginate = (view, activePage) =>
    tabHelpers.handleOnPaginate({view, activePage, tableData, setTableData, loadDataFunc: loadData, single: true});

  const handleOnFilter = (view, filterQuery) =>
    tabHelpers.handleOnFilter({view, filterQuery, loadDataFunc: loadData, queries, setQueries, single: true});

  const handleOnTableChange = (view, pagination, filters, sorter, extra) =>
    tabHelpers.handleOnTableChange({view, pagination, filters, sorter, extra, loadDataFunc: loadData});

  const handleOnAddClick = () => setInviteModalProps({...inviteModalProps, open: true});

  const handleErrorCallback = (response, validationKey) => {
    finishLoading();
    alertActions.error(response?.response?.data?.message || t(`teams:validation.${validationKey}`));
  }

  const handleResendInvite = (email, modalProps) => {
    Modal.confirm({
      ...modalProps,
      onOk() {
        startLoading();
        resendInvite({
          email,
          successCallback: () => {
            setTableData({
              ...tableData,
              rows: [...tableData.rows.map(r => r.email === email ? teamsHelpers.getReInvitedUserData(r) : r)]
            });
            finishLoading();
            logEvent(firebaseEvents.TEAM_USER_REINVITED);
          },
          errorCallback: (response) => handleErrorCallback(response, 'failedToResendInvitation')
        })
      }
    });
  }

  const handleCancelDisableModal = () => setIsOpenDisableModal(false);

  const handleOkDisableModal = (_, {terminate}) => {
    const email = selectedEmployee?.email || '';
    setIsOpenDisableModal(false);
    setActiveAction({
      ...activeAction,
      action: DISABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION,
      email,
      isTerminateSubscriptions: terminate
    });
    onDisableUser({action: DISABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION, email, terminate});
  }

  const handleDeleteUser = (email, modalProps) => {
    Modal.confirm({
      ...modalProps,
      onOk() {
        startLoading();
        deleteEmployee({
          email,
          successCallback: () => {
            loadData({initial: true});
            logEvent(firebaseEvents.TEAM_USER_DELETED);
            alertActions.success(t('teams:validation.userDeletedSuccessfully'));
          },
          errorCallback: (response) => handleErrorCallback(response, 'failedToDeleteUser')
        })
      }
    });
  }

  const handleDisableUser = (employee) => {
    setIsOpenDisableModal(true);
    setSelectedEmployee(employee);
  }

  const handleEnableUser = (email, modalProps) => {
    Modal.confirm({
      ...modalProps,
      onOk() {
        setActiveAction({...activeAction, action: ENABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION, email});
        onEnableUser({action: ENABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION, email});
      }
    });
  }

  const handleOnActionClick = ({e, action, employee}) => {
    e.stopPropagation();
    const email = employee?.email || '';
    const getConfirmModalProps = (props) => teamsHelpers.getConfirmModalProps({t, employee, ...props})

    const actions = {
      delete: () => {
        handleDeleteUser(
          email,
          getConfirmModalProps({action: 'delete'})
        );
      },
      disable: () => {
        handleDisableUser(
          employee,
          getConfirmModalProps({action: 'disable'})
        )
      },
      enable: () => {
        handleEnableUser(
          email,
          getConfirmModalProps({action: 'enable'})
        );
      },
      invite: () => {
        handleResendInvite(
          email,
          getConfirmModalProps({action: 'invite'})
        );
      },
      'resend-invite': () => {
        handleResendInvite(
          email,
          getConfirmModalProps({action: 'resend invitation'})
        );
      },
    }
    actions.hasOwnProperty(action) && actions[action]();
  }

  const handleUpdateEmployee = ({data, employee}) => {
    const email = gObjProp(employee, 'email');
    if (!email) return;

    updateEmployee({
      email,
      data,
      errorCallback: (response) => handleErrorCallback(response, 'failedToUpdateProperty')
    });
  }

  const handleOnRowClick = (e, record) => navigate(`${routes.teamUserPrefix}/${record.email}`);

  const getTabProps = (key, totalKey) => ({
    data: tableData?.rows || [],
    filtersRightSideContent: hasCreateAccess && (
      <Space size={0}>
        <StyledTableFiltersDivider type='vertical' />
        <Button
          onClick={handleOnAddClick}
          size='large'
          type='primary'
        >
          {trans('inviteUser')}
        </Button>
      </Space>
    ),
    onActionClick: handleOnActionClick,
    onUpdate: handleUpdateEmployee,
    onFilter: (value) => handleOnFilter(key, value),
    isEditorSupport: isSupport,
    editorEmail: employeeEmail,
    tableProps: {
      loading: loading[key] || false,
      onChange: (pagination, filters, sorter, extra) => handleOnTableChange(key, pagination, filters, sorter, extra),
      onRow: (record) => ({
        onClick: (e) => handleOnRowClick(e, record)
      }),
      pagination: tabHelpers.getTablePagination({handleOnPaginate, tableData, totalKey, single: true}),
    }
  });

  const handleCancelInviteModal = () => setInviteModalProps({...inviteModalProps, open: false});

  const handleOkInviteModal = (data, successCallback, errorCallback) => {
    setInviteModalProps({...inviteModalProps, loading: true});
    createEmployee({
      data,
      successCallback: (resp) => {
        setInviteModalProps({...inviteModalProps, ...DEFAULT_MODAL_PROPS});
        logEvent(firebaseEvents.TEAM_USER_INVITED);
        loadData({initial: true});
        successCallback && successCallback(resp);
      },
      errorCallback: (resp) => {
        setInviteModalProps({...inviteModalProps, loading: false});
        errorCallback && errorCallback(resp.response.data);
      }
    })
  }

  const scaResponseCallback = (response) => {
    scaHelpers.SCAResponseCallback({
      response,
      scaCallback: (scaProps) => setAuthWindowProps({...authWindowProps, ...scaProps}),
      errorCallback: finishLoading
    });
  }

  const updateEmployeeStatusSuccessCallback = ({email, logEventName, status}) => {
    if (queries.user_group !== ALL) {
      loadData({updateCurrentPage: true});
    } else {
      setTableData({
        ...tableData,
        rows: [...tableData.rows.map(row => ({...row, account_status: row.email !== email ? row.account_status : status}))]
      });
      finishLoading();
    }
    logEvent(logEventName);
  }

  const onDisableUser = ({
    action,
    email,
    terminate = undefined
  } = {}) => {
    const isTerminateUserSubscriptions = terminate !== undefined ? terminate : (activeAction.isTerminateSubscriptions || false);
    if (email === undefined) email = activeAction.email;
    startLoading();
    updateEmployeeStatus({
      headers: scaHelpers.getAuthHeaders(action || activeAction.action),
      email,
      data: {
        is_terminate_user_subscriptions: isTerminateUserSubscriptions,
        status: DISABLED_STATUS
      },
      successCallback: () => {
        updateEmployeeStatusSuccessCallback({email, logEventName: firebaseEvents.TEAM_USER_DISABLED, status: DISABLED_STATUS});
      },
      errorCallback: scaResponseCallback
    });
  }

  const onEnableUser = ({
    action,
    email
  } = {}) => {
    if (email === undefined) email = activeAction.email;
    startLoading();
    updateEmployeeStatus({
      headers: scaHelpers.getAuthHeaders(action || activeAction.action),
      email,
      data: {status: ENABLED_STATUS},
      successCallback: () => {
        updateEmployeeStatusSuccessCallback({email, logEventName: firebaseEvents.TEAM_USER_ENABLED, status: ENABLED_STATUS});
      },
      errorCallback: scaResponseCallback
    });
  }

  const handleCancelAuthModal = () => setAuthWindowProps({...authWindowProps, open: false});

  const onSuccessAuth = () => {
    const {action} = activeAction;
    const actions = {
      [DISABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION]: onDisableUser,
      [ENABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION]: onEnableUser,
    }
    handleCancelAuthModal();
    actions?.[action]();
  }

  return (
    <StyledTeam {...rest}>
      <AllTab {...getTabProps(ALL, 'all')} />

      <AddUserModal
        {...inviteModalProps}
        handleCancel={handleCancelInviteModal}
        handleOk={handleOkInviteModal}
      />

      <AuthenticationWindow
        {...authWindowProps}
        handleCancel={handleCancelAuthModal}
        onSuccess={onSuccessAuth}
        operationName={activeAction.action}
      />

      <DisableUserModal
        handleCancel={handleCancelDisableModal}
        handleOk={handleOkDisableModal}
        open={isOpenDisableModal}
      />
    </StyledTeam>
  );
}

const mapStateToProps = state => {
  const {employee, isSupport} = state.user;

  return {
    employeeEmail: employee.email,
    isSupport,
  }
}

const mapDispatchToProps = {
  createEmployee: teamActions.createEmployee,
  deleteEmployee: teamActions.deleteEmployee,
  getEmployees: teamActions.getEmployees,
  resendInvite: teamActions.resendInvite,
  updateEmployee: teamActions.updateEmployee,
  updateEmployeeStatus: teamActions.updateEmployeeStatus
}

export default connect(mapStateToProps, mapDispatchToProps)(Team);
